import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    loadPublicScreen,
    loadAllDashboards,
    createPlaylistListener,
    createScreenListener,
    createDashboardListener,
} from './actions/publicScreenActions';
import PublicScreenViewer from '../../../business/PublicScreenViewer/PublicScreenViewer';
import { LicenseService } from '../../../sdk/licenseService';
import { computeLicenseStatus } from '../../../utils/licenseUtils';

/**
 * PublicScreen - Container Component
 * מחבר את ה-routing ל-PublicScreenViewer
 * מטפל בכל הקריאות ל-Firestore והלוגיקה
 * 
 * Route: /:orgHash/:screenName
 * 
 * לוגיקת Timeouts:
 * 1. Timeout עד סוף המקטע הנוכחי -> מעבר למקטע הבא
 * 2. Timeout עד חצות (00:00) -> רענון יומי
 * 3. onSnapshot על המסך -> עדכון מיידי כשה-schedule משתנה
 */
const PublicScreen = () => {
    const { orgHash, screenName } = useParams();
    const dispatch = useDispatch();

    // Redux state
    const reduxPlaylist = useSelector(state => state.publicScreen.playlist);
    const reduxCatalog = useSelector(state => state.app.catalog); // ✅ קורא מ-app.catalog
    const reduxOrganization = useSelector(state => state.publicScreen.organization);
    const reduxDashboards = useSelector(state => state.publicScreen.dashboards);

    // Local state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentDashboard, setCurrentDashboard] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [playlistId, setPlaylistId] = useState(null);
    const [isLicenseExpired, setIsLicenseExpired] = useState(false);

    // Refs for timers
    const segmentTimeoutRef = useRef(null);
    const midnightTimeoutRef = useRef(null);
    const screenListenerRef = useRef(null);
    const playlistListenerRef = useRef(null);
    const dashboardListenersRef = useRef({}); // מפה של dashboardId -> unsubscribe function

    // חישוב זמן עד חצות
    const getTimeUntilMidnight = useCallback(() => {
        const now = new Date();
        const midnight = new Date();
        midnight.setDate(midnight.getDate() + 1);
        midnight.setHours(0, 0, 0, 0);
        return midnight - now;
    }, []);

    // פונקציה לניקוי כל ה-timers וה-listeners
    const cleanup = useCallback(() => {
        console.log('🧹 מנקה timers ו-listeners...');

        if (segmentTimeoutRef.current) {
            clearTimeout(segmentTimeoutRef.current);
            segmentTimeoutRef.current = null;
        }

        if (midnightTimeoutRef.current) {
            clearTimeout(midnightTimeoutRef.current);
            midnightTimeoutRef.current = null;
        }

        if (screenListenerRef.current) {
            screenListenerRef.current();
            screenListenerRef.current = null;
        }

        if (playlistListenerRef.current) {
            playlistListenerRef.current();
            playlistListenerRef.current = null;
        }

        // ניקוי כל ה-listeners של הדשבורדים
        Object.values(dashboardListenersRef.current).forEach(unsubscribe => {
            if (unsubscribe) unsubscribe();
        });
        dashboardListenersRef.current = {};
    }, []);

    // פונקציה לטעינת המסך והגדרת timers
    const loadScreenAndSetupTimers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('🔄 טוען מסך ומגדיר timers...');

            // 1. טעינת המסך, פלייליסט ודשבורד ראשון
            const { screen, playlist, dashboard, activeScheduleItem } = await dispatch(loadPublicScreen(orgHash, screenName));

            setPlaylistId(playlist.playlistId);
            setCurrentDashboard(dashboard);
            // מצא את האינדקס של הדשבורד הראשון הפעיל (שנבחר ב-loadPublicScreen)
            const firstEnabledIdx = playlist.items.findIndex(
                item => item.dashboardId === dashboard.dashboardId
            );
            setCurrentIndex(firstEnabledIdx >= 0 ? firstEnabledIdx : 0);

            // 2. טעינת כל הדשבורדים מראש
            await dispatch(loadAllDashboards(playlist.items));

            // 3. הגדרת Timeout עד סוף המקטע הנוכחי
            if (activeScheduleItem && activeScheduleItem.timeUntilEnd > 0) {
                const minutesLeft = Math.floor(activeScheduleItem.timeUntilEnd / 1000 / 60);
                console.log('⏰ מגדיר timeout עד סוף המקטע בעוד', minutesLeft, 'דקות');
                console.log('📅 מקטע נוכחי:', `${activeScheduleItem.startTime} - ${activeScheduleItem.endTime}`);
                console.log('🔜 כשה-timeout יסתיים, המערכת תבדוק אוטומטית את המקטע הבא לפי השעה החדשה');

                segmentTimeoutRef.current = setTimeout(() => {
                    console.log('⏰ Timeout הסתיים!');
                    console.log('🔄 בודק מה המקטע הנוכחי לפי היום והשעה החדשים...');
                    loadScreenAndSetupTimers();
                }, activeScheduleItem.timeUntilEnd);
            }

            // 4. הגדרת Timeout עד חצות (00:00)
            const timeUntilMidnight = getTimeUntilMidnight();
            console.log('🌙 מגדיר timeout עד חצות בעוד', Math.floor(timeUntilMidnight / 1000 / 60 / 60), 'שעות');

            midnightTimeoutRef.current = setTimeout(() => {
                console.log('🌅 חצות! רענון יומי...');
                loadScreenAndSetupTimers();
            }, timeUntilMidnight);

            // 5. הגדרת Listener למסך (onSnapshot)
            if (!screenListenerRef.current) {
                screenListenerRef.current = createScreenListener(screen.screenId, (newSchedule) => {
                    console.log('🔥 Schedule השתנה! טוען מסך מחדש...');
                    cleanup();
                    loadScreenAndSetupTimers();
                });
            }

            setLoading(false);
        } catch (err) {
            console.error('❌ שגיאה בטעינת המסך:', err);
            setError(err.message || 'שגיאה בטעינת המסך');
            setLoading(false);
        }
    }, [dispatch, orgHash, screenName, getTimeUntilMidnight, cleanup]);

    // טעינה ראשונית
    useEffect(() => {
        console.log('🎬 useEffect רץ - orgHash:', orgHash, 'screenName:', screenName);

        if (orgHash && screenName) {
            loadScreenAndSetupTimers();
        }

        // ניקוי בעת unmount
        return () => {
            console.log('🛑 Component unmounting - מנקה...');
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgHash, screenName]);

    // 🔥 Realtime Listener לפלייליסט - מאזין לשינויים
    useEffect(() => {
        if (!playlistId) return;

        // יצירת listener דרך ה-action
        playlistListenerRef.current = createPlaylistListener(playlistId, dispatch);

        // ניקוי ה-listener כשהקומפוננטה נהרסת או playlistId משתנה
        return () => {
            if (playlistListenerRef.current) {
                console.log('🔌 מנתק listener מהפלייליסט');
                playlistListenerRef.current();
                playlistListenerRef.current = null;
            }
        };
    }, [playlistId, dispatch]);

    // 🔥 Realtime Listeners לדשבורדים - מאזין לשינויים בכל דשבורד
    useEffect(() => {
        if (!reduxPlaylist || !reduxPlaylist.items) return;

        // ניקוי listeners ישנים
        Object.values(dashboardListenersRef.current).forEach(unsubscribe => {
            if (unsubscribe) unsubscribe();
        });
        dashboardListenersRef.current = {};

        // יצירת listener לכל דשבורד בפלייליסט
        reduxPlaylist.items.forEach(item => {
            const dashboardId = item.dashboardId;
            console.log('🎯 יוצר listener לדשבורד:', dashboardId);
            dashboardListenersRef.current[dashboardId] = createDashboardListener(dashboardId, dispatch);
        });

        // ניקוי כשהפלייליסט משתנה
        return () => {
            Object.values(dashboardListenersRef.current).forEach(unsubscribe => {
                if (unsubscribe) unsubscribe();
            });
            dashboardListenersRef.current = {};
        };
    }, [reduxPlaylist, dispatch]);

    // מאזין לעדכוני הפלייליסט מה-Redux ומטפל בשינויים
    useEffect(() => {
        if (!reduxPlaylist || !currentDashboard) return;

        // בודק אם הדשבורד הנוכחי עדיין קיים בפלייליסט
        const currentDashboardExists = reduxPlaylist.items?.some(
            item => item.dashboardId === currentDashboard?.dashboardId
        );

        // אם הדשבורד נמחק או הושבת, עובר לראשון פעיל
        const currentItemDisabled = reduxPlaylist.items?.find(
            item => item.dashboardId === currentDashboard?.dashboardId
        )?.enabled === false;

        if ((!currentDashboardExists || currentItemDisabled) && reduxPlaylist.items?.length > 0) {
            console.log('⚠️ הדשבורד הנוכחי נמחק או הושבת, עובר לראשון פעיל');
            const firstEnabledIndex = reduxPlaylist.items.findIndex(item => item.enabled !== false);
            if (firstEnabledIndex >= 0) {
                const firstDashboardId = reduxPlaylist.items[firstEnabledIndex].dashboardId;
                const firstDashboard = reduxDashboards[firstDashboardId];
                if (firstDashboard) {
                    setCurrentDashboard(firstDashboard);
                    setCurrentIndex(firstEnabledIndex);
                }
            }
        }
    }, [reduxPlaylist, currentDashboard, reduxDashboards]);

    // 🔥 מאזין לעדכונים בדשבורד הנוכחי מ-Redux ומעדכן את ה-local state
    useEffect(() => {
        if (!currentDashboard || !reduxDashboards) return;

        // בודק אם יש גירסה מעודכנת של הדשבורד הנוכחי ב-Redux
        const updatedDashboard = reduxDashboards[currentDashboard.dashboardId];

        if (!updatedDashboard) return;

        // השווה לפי מספר הגירסאות או versionId האחרון
        const currentVersionsCount = currentDashboard.versions?.length || 0;
        const updatedVersionsCount = updatedDashboard.versions?.length || 0;
        const currentLatestVersionId = currentDashboard.versions?.[currentVersionsCount - 1]?.versionId;
        const updatedLatestVersionId = updatedDashboard.versions?.[updatedVersionsCount - 1]?.versionId;

        // עדכן רק אם יש גירסה חדשה
        if (updatedLatestVersionId && updatedLatestVersionId !== currentLatestVersionId) {
            setCurrentDashboard(updatedDashboard);
        }
    }, [reduxDashboards, currentDashboard]);

    // מעבר אוטומטי בין דשבורדים
    useEffect(() => {
        if (!reduxPlaylist || !reduxPlaylist.items || reduxPlaylist.items.length <= 1) {
            return;
        }

        const currentItem = reduxPlaylist.items[currentIndex];
        const duration = (currentItem?.duration || 30) * 1000;

        console.log(`⏱️ מעבר לדשבורד הבא בעוד ${duration / 1000} שניות`);

        const timer = setTimeout(() => {
            // מחפש את הדשבורד הבא שפורסם
            let nextIndex = (currentIndex + 1) % reduxPlaylist.items.length;
            let attempts = 0;
            const maxAttempts = reduxPlaylist.items.length;

            while (attempts < maxAttempts) {
                const nextItem = reduxPlaylist.items[nextIndex];
                const nextDashboardId = nextItem.dashboardId;
                const nextDashboard = reduxDashboards[nextDashboardId];

                // בודק אם הדשבורד פעיל, קיים ופורסם
                if (nextItem.enabled === false) {
                    console.log(`⏭️ דשבורד ${nextIndex + 1} מושבת, מדלג`);
                    nextIndex = (nextIndex + 1) % reduxPlaylist.items.length;
                    attempts++;
                } else if (nextDashboard && nextDashboard.versions && nextDashboard.versions.length > 0) {
                    console.log(`➡️ עובר לדשבורד ${nextIndex + 1}/${reduxPlaylist.items.length}`);
                    setCurrentDashboard(nextDashboard);
                    setCurrentIndex(nextIndex);
                    break;
                } else {
                    console.log(`⚠️ דשבורד ${nextIndex + 1} לא פורסם, מדלג`);
                    nextIndex = (nextIndex + 1) % reduxPlaylist.items.length;
                    attempts++;
                }
            }

            if (attempts >= maxAttempts) {
                console.error('❌ אין דשבורדים פורסמים בפלייליסט');
            }
        }, duration);

        return () => clearTimeout(timer);
    }, [reduxPlaylist, currentIndex, reduxDashboards]);

    // ריענון מלא ב-3 לפנות בוקר - שחרור זיכרון
    useEffect(() => {
        const now = new Date();
        const target = new Date();
        target.setHours(3, 0, 0, 0);
        if (now >= target) {
            target.setDate(target.getDate() + 1);
        }
        const ms = target - now;

        console.log('🔄 ריענון מלא מתוכנן ב-3:00, בעוד', Math.floor(ms / 1000 / 60 / 60), 'שעות');

        const timer = setTimeout(() => {
            console.log('🔄 3:00 - ריענון מלא...');
            window.location.reload();
        }, ms);

        return () => clearTimeout(timer);
    }, []);

    // בדיקת תוקף רישיון הארגון
    useEffect(() => {
        if (!reduxOrganization?.organizationId) return;
        LicenseService.getLicense(reduxOrganization.organizationId)
            .then(rawLicense => {
                const license = computeLicenseStatus(rawLicense);
                if (license?.isExpired) setIsLicenseExpired(true);
            })
            .catch(() => {});
    }, [reduxOrganization?.organizationId]);

    // קבלת האנימציה של הדשבורד הנוכחי
    const getCurrentTransition = () => {
        if (!reduxPlaylist || !reduxPlaylist.items || currentIndex >= reduxPlaylist.items.length) {
            return 'fade';
        }
        return reduxPlaylist.items[currentIndex]?.transition || 'fade';
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <PublicScreenViewer
                loading={loading}
                error={error}
                currentDashboard={currentDashboard}
                transition={getCurrentTransition()}
                organization={reduxOrganization}
                currentIndex={currentIndex}
                totalDashboards={reduxPlaylist?.items?.length || 0}
                dispatch={dispatch}
                catalog={reduxCatalog}
            />
            {/* Branding badge */}
            <a
                href="https://inorai.web.app"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    position: 'fixed',
                    bottom: isLicenseExpired ? 50 : 12,
                    left: 12,
                    zIndex: 9998,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 5px',
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    borderRadius: '4px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                    textDecoration: 'none',
                    opacity: 0.4,
                    transition: 'opacity 0.2s, transform 0.2s',
                    fontSize: '0.45rem',
                    color: '#1e3a5f',
                    fontFamily: 'Arial, sans-serif',
                    direction: 'rtl',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.transform = 'scale(1.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
                <img src="/inorai-logo.png" alt="inorai" style={{ height: 10, width: 'auto', borderRadius: 2 }} />
                <span><strong>inorai</strong>.web.app</span>
            </a>
            {isLicenseExpired && (
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(30, 58, 95, 0.92)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    padding: '10px 24px',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '1rem',
                    direction: 'rtl',
                }}>
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <span style={{ fontWeight: 700 }}>מנוי inorai פג תוקף</span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>|</span>
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                        לחידוש המנוי צרו קשר: ineraigroup@gmail.com
                    </span>
                </div>
            )}
        </div>
    );
};

export default PublicScreen;
